// 261015 P4c-1 — MỌI quyết định hiển thị của tab "Tổng quan truyện" (hàm thuần, có jest). KHÔNG import antd / react (jest của react-scripts không load được antd v6).
// Component chỉ vẽ cái file này trả về. Nguồn dữ liệu: `OutlineRecord.outline.overview` + `OutlineRecord.overviewState` (BE tính lúc đọc).
import type {
  FilmOutline, MotiveGroup, OutlineCharacter, OutlineRecord, Overview, OverviewClaim, OverviewDisposition, OverviewEstimate, OverviewFaction, OverviewJobResult,
  OverviewPlotKey, OverviewProfile, OverviewRelationship, OverviewRelationshipType, OverviewStateView,
} from '../../../../types/adaptation';
import type { JobStepProgress } from '../../../../types/adaptationWorkbench';
import { buildNamePairs, profileTitle, swapNames, type NameMode, type NamePair } from './overviewNames';
import type { GraphEdge, GraphGroup, GraphInput, GraphNode } from './relationGraphLayout';

export type OverviewMode = 'full' | 'force' | 'merge';

const str = (v: unknown): string => String(v ?? '').trim();
const noMarks = (s: string): string => str(s).normalize('NFD').replace(/[̀-ͯ]/g, '').replace(/đ/g, 'd').replace(/Đ/g, 'D').toLocaleLowerCase('vi');

// ─── Nhãn ─────────────────────────────────────────────────────────────────────────────────────────────────

export const PLOT_KEYS: ReadonlyArray<OverviewPlotKey> = ['beginning', 'continuation', 'turn', 'ending'];
export const PLOT_LABEL: Record<OverviewPlotKey, string> = { beginning: '1 · Mở đầu', continuation: '2 · Tiếp diễn', turn: '3 · Bước ngoặt', ending: '4 · Kết thúc' };
export const PLOT_STATUS_TEXT: Record<'KNOWN' | 'NOT_REACHED' | 'UNKNOWN', string> = {
  KNOWN: 'Đã có trong nguồn', NOT_REACHED: 'Nguồn chưa tới', UNKNOWN: 'Chưa đủ ý để nói',
};
export const CLAIM_KIND_LABEL: Record<string, string> = { PLOT: 'Sự việc', CHARACTER: 'Nhân vật', RELATION: 'Quan hệ', PROP: 'Đạo cụ', RULE: 'Luật thế giới' };
const REASON_TEXT: Record<string, string> = {
  QUOTE_NOT_FOUND: 'không tìm thấy trích này trong nguyên tác',
  QUOTE_AMBIGUOUS: 'trích khớp nhiều chỗ trong nguyên tác — chép dài hơn để chỉ khớp một chỗ',
  QUOTE_TOO_SHORT: 'trích quá ngắn để định vị',
  QUOTE_TOO_LONG: 'trích quá dài (tối đa 1 600 ký tự)',
  NO_QUOTE: 'ý chưa có trích nào',
  QUOTE_OUT_OF_SCOPE: 'trích nằm ngoài các chương của tổng quan',
};
export const reasonText = (code: string | undefined | null): string => (code ? REASON_TEXT[code] ?? `chưa xác minh (${code})` : '');

export const DISPOSITION_TEXT: Record<OverviewDisposition, string> = {
  KEPT: 'Giữ trong phim', MERGED: 'Đã gộp vào nhân vật khác', CUT: 'Đã cắt khỏi phim', UNMAPPED: 'Chưa có trong dàn nhân vật (có thể đã cắt)',
};
export const DISPOSITION_TONE: Record<OverviewDisposition, 'ok' | 'warn' | 'bad' | 'neutral'> = { KEPT: 'ok', MERGED: 'warn', CUT: 'bad', UNMAPPED: 'neutral' };
export const TIER_TEXT: Record<string, string> = { lead: 'Nhân vật chính', support: 'Vai phụ', functional: 'Chức năng', none: 'Chưa có trong dàn nhân vật' };
const TIER_RANK: Record<string, number> = { lead: 0, support: 1, functional: 2 };

// ─── Ý (claim) + trích ────────────────────────────────────────────────────────────────────────────────────

export interface ClaimBadge { tone: 'ok' | 'bad' | 'warn'; text: string }
export interface ClaimView {
  id: string; text: string; kindText: string; status: OverviewClaim['status']; badge: ClaimBadge; reason: string; quoteCount: number;
  /** "Quyển 2" hoặc "Bạn thêm tay". */
  originText: string; manual: boolean;
}

/** Huy hiệu của ý. 🔴 KHÔNG nói "đã xác minh sự thật" — code chỉ chứng minh trích CÓ THẬT trong nguyên tác (D3). */
export const claimBadge = (c: Pick<OverviewClaim, 'status' | 'anchorIds'>): ClaimBadge => {
  const n = c.anchorIds?.length ?? 0;
  if (c.status === 'ok') return { tone: 'ok', text: n > 0 ? `đã tìm thấy ${n} trích` : 'đã tìm thấy trích' };
  if (c.status === 'stale') return { tone: 'warn', text: 'trích đã cũ (chương đã sửa)' };
  return { tone: 'bad', text: 'chưa xác minh' };
};

export const claimMap = (ov: Overview | null | undefined): Map<string, OverviewClaim> => new Map((ov?.claims ?? []).map((c) => [c.id, c] as const));

export const claimView = (c: OverviewClaim, pairs: ReadonlyArray<NamePair> = []): ClaimView => ({
  id: c.id, text: swapNames(c.text, pairs), kindText: CLAIM_KIND_LABEL[c.kind] ?? c.kind, status: c.status, badge: claimBadge(c),
  reason: c.status === 'ok' ? '' : reasonText(c.reason), quoteCount: c.anchorIds?.length ?? 0,
  originText: c.volumeIndex > 0 ? `Quyển ${c.volumeIndex}` : 'Bạn thêm tay', manual: c.volumeIndex === 0,
});

/** Id ý → ClaimView theo thứ tự id đưa vào; id không còn trong kho (tham chiếu hỏng) bị bỏ — `missing` đếm chúng để báo. */
export const claimsOf = (ov: Overview | null | undefined, ids: ReadonlyArray<string> | undefined, pairs: ReadonlyArray<NamePair> = []): { claims: ClaimView[]; missing: number } => {
  const m = claimMap(ov);
  const claims: ClaimView[] = [];
  let missing = 0;
  for (const id of ids ?? []) { const c = m.get(id); if (c) claims.push(claimView(c, pairs)); else missing += 1; }
  return { claims, missing };
};

export interface EvidenceQuote { anchorId: string; chapter: number; quote: string }
export interface EvidenceView {
  claimId: string; text: string; statusText: string; tone: 'ok' | 'bad' | 'warn'; reason: string; originText: string;
  quotes: EvidenceQuote[]; unlocated: string[];
  /** Câu trung thực cố định hiện ở ngăn kéo — ranh giới của "đã tìm thấy trích". */
  honesty: string;
}
export const EVIDENCE_HONESTY = 'Tìm thấy trích không có nghĩa ý đã đúng nghĩa. Hãy đọc cả ý và trích: ý có thêm chi tiết, đổi người làm hoặc chắc chắn hơn trích không?';

/** Nội dung ngăn kéo xem trích. Trích = chữ GỐC của nguyên tác (không bao giờ bị thay tên). `null` nếu ý không tồn tại. */
export const evidenceView = (ov: Overview | null | undefined, claimId: string, pairs: ReadonlyArray<NamePair> = []): EvidenceView | null => {
  const c = claimMap(ov).get(claimId);
  if (!c || !ov) return null;
  const anchors = new Map((ov.anchors ?? []).map((a) => [a.id, a] as const));
  const v = claimView(c, pairs);
  return {
    claimId: c.id, text: v.text, statusText: v.badge.text, tone: v.badge.tone, reason: v.reason, originText: v.originText,
    quotes: (c.anchorIds ?? []).map((id) => anchors.get(id)).filter((a): a is NonNullable<typeof a> => !!a).map((a) => ({ anchorId: a.id, chapter: a.orderIndex, quote: a.quote })),
    unlocated: c.unlocatedQuotes ?? [], honesty: EVIDENCE_HONESTY,
  };
};

// ─── Động cơ hấp dẫn (261015 P4c-2a) ─────────────────────────────────────────────────────────────────────────

/** Nhãn nói rõ BẢN CHẤT: bàn tay vàng là SỰ VIỆC có trích (code kiểm); ba nhóm kia là GHI CHÚ BIÊN KỊCH do AI đề xuất (không phải sự việc nguồn, không được thêm tình tiết). */
export const MOTIVE_FACT_LABEL = 'Sự việc có nguồn';
export const MOTIVE_NOTE_LABEL = 'Đề xuất biên kịch';
export const MOTIVE_NOTE_HONESTY = 'Ghi chú định hướng cách kể; không phải sự kiện mới được phép thêm vào phim. Mỗi ghi chú dựa trên các ý nền bên dưới — đọc ý nền và trích để xem ghi chú có bám đúng không.';
export const GOLDEN_FINGER_HONESTY = 'Trích có thật không đồng nghĩa mọi diễn giải đều đúng. Truyện không có lợi thế đặc biệt thì để trống — không ép.';
/** FE nói thật về khả năng của Trợ lý ② (D6): chưa có lệnh nào sửa phần này. */
export const MOTIVES_EDIT_NOTE = 'Sửa phần Động cơ hấp dẫn bằng form (trợ lý chưa sửa phần này).';
export const MOTIVE_NOTES_MAX = 5;
export const GOLDEN_FINGER_MAX = 20;

export const MOTIVE_GROUPS: ReadonlyArray<{ group: MotiveGroup; label: string; short: string; help: string }> = [
  { group: 'satisfactionMode', label: 'Kiểu thoả mãn', short: 'kiểu thoả mãn', help: 'Người xem được thoả mãn theo kiểu nào (vd lật thế, được công nhận, trả đũa).' },
  { group: 'suspenseDrivers', label: 'Động lực hồi hộp', short: 'động lực hồi hộp', help: 'Điều khiến người xem muốn xem tiếp (bí mật, nguy hiểm, câu hỏi chưa có đáp).' },
  { group: 'sellingPoints', label: 'Điểm hấp dẫn của truyện', short: 'điểm hấp dẫn', help: 'Điều khiến truyện này khác truyện khác.' },
];

// ─── Danh sách ý được tham chiếu (khớp BE `referencedClaimIds`) ──────────────────────────────────────────────────

export interface ClaimListRef { label: string; ids: string[] }

/** MỌI chỗ mang `claimId` + nhãn người đọc được (dùng cho "ý này đang được dùng ở …" khi xoá). */
export const claimLists = (ov: Overview | null | undefined): ClaimListRef[] => {
  if (!ov) return [];
  const out: ClaimListRef[] = [{ label: 'Tóm tắt', ids: ov.synopsis ?? [] }];
  for (const k of PLOT_KEYS) out.push({ label: `Đoạn ${PLOT_LABEL[k]}`, ids: ov.plot?.[k]?.claimIds ?? [] });
  for (const p of ov.profiles ?? []) {
    const w = `Hồ sơ ${p.sourceName}`;
    out.push({ label: `${w} · tính cách`, ids: p.personality }, { label: `${w} · bằng chứng hành vi`, ids: p.behavior ?? [] }, { label: `${w} · xuất thân`, ids: p.setup }, { label: `${w} · vai`, ids: p.roleLabel }, { label: `${w} · chức danh`, ids: p.roleTitle },
      { label: `${w} · ngoại hình hiện tại`, ids: p.appearance?.current ?? [] }, { label: `${w} · ngoại hình trước`, ids: p.appearance?.before ?? [] }, { label: `${w} · ngoại hình sau`, ids: p.appearance?.after ?? [] });
  }
  for (const i of ov.firstEpisodeStrategy?.introductions ?? []) out.push({ label: 'Chiến lược tập 1', ids: i.basisClaimIds });
  for (const p of ov.keyProps ?? []) out.push({ label: `Đạo cụ ${p.name}`, ids: p.description });
  for (const w of ov.worldRules ?? []) out.push({ label: `Luật ${w.title}`, ids: w.claimIds });
  for (const r of ov.relationships ?? []) out.push({ label: `Quan hệ ${r.id}`, ids: r.claimIds });
  for (const f of ov.factions ?? []) out.push({ label: `Phe ${f.label}`, ids: f.claimIds });
  for (const l of ov.introLines ?? []) out.push({ label: `Câu giới thiệu ${l.id}`, ids: l.basisClaimIds });
  // 261015 P4c-2a (khớp BE `claimLists`/`referencedClaimIds`): bàn tay vàng + ý nền của từng ghi chú động cơ.
  const m = ov.motives;
  if (m) {
    out.push({ label: 'Bàn tay vàng', ids: m.goldenFinger?.claimIds ?? [] });
    for (const g of MOTIVE_GROUPS) for (const n of m[g.group] ?? []) out.push({ label: `Ghi chú ${g.short} ${n.id}`, ids: n.basisClaimIds ?? [] });
  }
  return out;
};

export const referencedClaimIds = (ov: Overview | null | undefined): Set<string> => new Set(claimLists(ov).flatMap((l) => l.ids));

/** Ý đang được tham chiếu mà KHÔNG `ok` — đúng thứ BE đếm ở `unverifiedCount` và chặn chốt (409 `OVERVIEW_UNVERIFIED`). */
export const unverifiedReferenced = (ov: Overview | null | undefined): OverviewClaim[] => {
  const used = referencedClaimIds(ov);
  return (ov?.claims ?? []).filter((c) => c.status !== 'ok' && used.has(c.id));
};

/** Nhãn các chỗ đang dùng ý này (không trùng). */
export const claimUsage = (ov: Overview | null | undefined, claimId: string): string[] =>
  Array.from(new Set(claimLists(ov).filter((l) => l.ids.includes(claimId)).map((l) => l.label)));

export const hasManualClaims = (ov: Overview | null | undefined): boolean => (ov?.claims ?? []).some((c) => c.volumeIndex === 0);

// ─── Phạm vi nguồn ────────────────────────────────────────────────────────────────────────────────────────

export interface VolumeLine { index: number; status: 'DONE' | 'FAILED' | 'MISSING'; claimCount: number; text: string }
export interface SourceScopeView {
  chaptersText: string; completeness: 'UNKNOWN' | 'INCOMPLETE' | 'COMPLETE'; completenessText: string; volumes: VolumeLine[]; missingVolumes: number[]; emptyVolumes: number[];
}

/** Quyển ĐỌC XONG nhưng không có ý nào (AI trả mảng rỗng thật) — KHÔNG phải quyển thiếu; hiện bền vững từ dữ liệu đã lưu (không cần kết quả job). */
export const emptyVolumesOf = (ov: Overview | null | undefined): number[] =>
  (ov?.source?.volumes ?? []).filter((v) => v.status === 'DONE' && v.claimCount === 0).map((v) => v.volumeIndex).sort((a, b) => a - b);

export const sourceScopeView = (ov: Overview, missingFromState?: number[]): SourceScopeView => {
  const s = ov.source;
  const have = new Map((s?.volumes ?? []).map((v) => [v.volumeIndex, v] as const));
  const planIdx = (s?.plan ?? []).map((p) => p.volumeIndex);
  const all = Array.from(new Set([...planIdx, ...Array.from(have.keys())])).sort((a, b) => a - b);
  const volumes: VolumeLine[] = all.map((i) => {
    const v = have.get(i);
    if (!v) return { index: i, status: 'MISSING' as const, claimCount: 0, text: `Quyển ${i}: chưa đọc` };
    if (v.status === 'FAILED') return { index: i, status: 'FAILED' as const, claimCount: 0, text: `Quyển ${i}: đọc lỗi` };
    return { index: i, status: 'DONE' as const, claimCount: v.claimCount, text: v.claimCount === 0 ? `Quyển ${i}: đọc xong, không có ý nào` : `Quyển ${i}: ${v.claimCount} ý` };
  });
  const missing = missingFromState ?? volumes.filter((v) => v.status !== 'DONE').map((v) => v.index);
  const chapters = s && s.lastOrder > 0 ? (s.firstOrder === s.lastOrder ? `chương ${s.lastOrder}` : `chương ${s.firstOrder}–${s.lastOrder}`) : 'chưa rõ phạm vi';
  return {
    chaptersText: `Bản đọc từ ${chapters}${s?.chapterCount ? ` (${s.chapterCount} chương)` : ''}`,
    completeness: s?.completeness ?? 'UNKNOWN', completenessText: str(s?.completenessNote), volumes, missingVolumes: missing, emptyVolumes: emptyVolumesOf(ov),
  };
};

export const overviewCounts = (ov: Overview): string => {
  const parts = [`${ov.profiles?.length ?? 0} nhân vật`, `${ov.keyProps?.length ?? 0} đạo cụ`, `${ov.worldRules?.length ?? 0} luật thế giới`, `${ov.claims?.length ?? 0} ý có trích`];
  return parts.join(' · ');
};

// ─── Trạng thái trang ─────────────────────────────────────────────────────────────────────────────────────

export type OverviewPanelKind = 'running' | 'failed' | 'empty' | 'ready';
export interface JobLike { jobId: string; progress?: JobStepProgress; startedAt?: string | null }
export interface FailedJobLike { jobId: string; note?: string; endedAt?: string | null; progress?: JobStepProgress }

/** Thứ tự ưu tiên: đang chạy → (chưa có tổng quan: lỗi / trống) → đọc được. Job lỗi cũ hơn lần tạo gần nhất của tổng quan không được tính. */
export const panelKind = (hasOverview: boolean, running: JobLike | null | undefined, failed: FailedJobLike | null | undefined): OverviewPanelKind => {
  if (running) return 'running';
  if (!hasOverview) return failed ? 'failed' : 'empty';
  return 'ready';
};

/** Mốc muộn nhất mà tổng quan được ghi (lần đọc quyển / gộp gần nhất) — ms; 0 nếu không rõ. */
export const lastGeneratedMs = (ov: Overview | null | undefined): number => {
  const ts = [...(ov?.source?.volumes ?? []).map((v) => Date.parse(v.extractedAt)), ...((ov?.generatedWith ?? []) as Array<{ at?: string }>).map((g) => Date.parse(String(g?.at ?? '')))].filter((n) => Number.isFinite(n));
  return ts.length ? Math.max(...ts) : 0;
};

/** Job lỗi có còn đáng hiện cạnh một tổng quan sẵn có không (lỗi SAU lần tạo gần nhất). */
export const failureMatters = (ov: Overview | null | undefined, failed: FailedJobLike | null | undefined): boolean => {
  if (!failed) return false;
  if (!ov) return true;
  const t = Date.parse(str(failed.endedAt));
  return !Number.isFinite(t) || t > lastGeneratedMs(ov);
};

export type BannerTone = 'error' | 'warning' | 'info' | 'success';
export type BannerAction = 'regenerate' | 'merge' | 'unverified' | null;
export interface Banner { key: string; tone: BannerTone; title: string; text: string; action: BannerAction; actionLabel: string; actionDisabledReason: string | null }

const dateText = (iso: string | undefined): string => {
  const t = Date.parse(str(iso));
  if (!Number.isFinite(t)) return '';
  const d = new Date(t);
  const p = (n: number) => String(n).padStart(2, '0');
  return `${p(d.getDate())}/${p(d.getMonth() + 1)}/${d.getFullYear()}`;
};

export const STALE_CONTENT_TEXT = 'Nguyên tác đã đổi/dài thêm — tạo lại tổng quan cho phần mới.';
export const STALE_READER_TEXT = 'Cách đọc đã cập nhật — tạo lại để dùng bản mới.';
export const SKELETON_CHANGED_TEXT = 'Khung xương đã đổi sau khi gộp tổng quan — bấm "Gộp lại tổng quan" (chỉ gộp lại, không đọc lại nguyên tác).';
/** Khớp BE `overviewApproval.MERGE_OUTDATED_TEXT` (mã `OVERVIEW_MERGE_OUTDATED`). */
export const MERGE_OUTDATED_TEXT = 'Cách gộp đã cập nhật — bấm "Gộp lại tổng quan"';
export const MERGE_OUTDATED_TITLE = 'Tổng quan này gộp theo cách cũ — bấm Gộp lại để có Động cơ hấp dẫn';
/** Khi nguồn/cách đọc cũng đang lệch (`stale === true`) thì "Gộp lại" một mình bị BE từ chối ⇒ chỉ MỘT lối ra: Tạo lại (cũng gộp theo cách mới). */
export const MERGE_OUTDATED_WITH_STALE = 'Tạo lại cũng đem lại Động cơ hấp dẫn.';
export const MERGE_BLOCKED_BY_STALE = 'Nguyên tác hoặc cách đọc đã đổi — "Gộp lại" một mình bị từ chối; hãy "Tạo lại" trước.';

/**
 * Các dải cảnh báo ĐẦU trang tổng quan, theo thứ tự cần xử lý. `approved` lấy từ `overviewState.approved` (biên nhận khớp ba dấu), KHÔNG từ `record.status`.
 * `stale === null` = chưa biết (không nạp được nguồn) — KHÔNG được coi là "không lệch": hiện thông tin trung lập.
 */
export const overviewBanners = (record: OutlineRecord, failed?: FailedJobLike | null): Banner[] => {
  const ov = record.outline?.overview;
  const st = record.overviewState;
  if (!ov) return [];
  const out: Banner[] = [];
  const mk = (key: string, tone: BannerTone, title: string, text: string, action: BannerAction = null, actionLabel = '', actionDisabledReason: string | null = null): void => {
    out.push({ key, tone, title, text, action, actionLabel, actionDisabledReason });
  };
  const locked = record.status === 'APPROVED';
  if (locked) {
    const a = ov.approval;
    if (st && st.sourceChecked && !st.approved) mk('approved-stale', 'warning', 'Bản đã chốt không còn khớp', 'Nội dung tổng quan, khung xương hoặc nguồn đã đổi so với lúc chốt — bỏ chốt rồi chốt lại.');
    else mk('approved', 'success', `Đã chốt${a ? ` bởi ${str(a.byName) || str(a.by)}${dateText(a.at) ? ` · ${dateText(a.at)}` : ''}` : ''}`, 'Nội dung đang khoá. Bạn có thể chia tập. Muốn sửa, bấm "Mở để sửa" — cần chốt lại sau đó; các tập đã có không tự đổi.');
  }
  const missing = st?.missingVolumes ?? [];
  if (missing.length) mk('missing', 'error', `Thiếu quyển ${missing.join(', ')}`, 'Các quyển này chưa đọc được nên tổng quan chưa đủ; chưa chốt được. "Tạo lại" chỉ đọc các quyển thiếu.', 'regenerate', 'Tạo lại');
  const causes = st?.staleCauses ?? [];
  if (st?.stale === true) {
    if (causes.includes('CONTENT')) mk('stale-content', 'warning', STALE_CONTENT_TEXT, 'Chặn chốt cho tới khi tạo lại. Chỉ các quyển đổi/mới được đọc lại.', 'regenerate', 'Tạo lại');
    if (causes.includes('READER')) mk('stale-reader', 'warning', STALE_READER_TEXT, 'Chặn chốt cho tới khi tạo lại.', 'regenerate', 'Tạo lại');
    if (!causes.length) mk('stale', 'warning', 'Nguyên tác hoặc cách đọc đã đổi', 'Tạo lại tổng quan để cập nhật.', 'regenerate', 'Tạo lại');
    // 261015 P4c-2a (Q7): lệch nguồn + gộp cũ ⇒ GỘP thông điệp vào dải stale (một lối ra: Tạo lại), KHÔNG hiện dải/nút "Gộp lại" riêng (BE từ chối merge khi nguồn lệch).
    if (st.mergeOutdated) { const first = out.find((b) => b.key.startsWith('stale')); if (first) first.text = `${first.text} ${MERGE_OUTDATED_WITH_STALE}`.trim(); }
  } else if (st && st.stale === null) {
    mk('stale-unknown', 'info', 'Chưa kiểm được nguyên tác lúc này', 'Không nạp được chương để đối chiếu; chưa biết tổng quan có lệch nguồn không (khi chốt, hệ thống sẽ kiểm lại).');
  }
  // Đúng thứ tự BE (`OVERVIEW_CODE_ORDER`): SOURCE_STALE → MERGE_OUTDATED → SKELETON_CHANGED. CHỈ khi nguồn không lệch (`stale !== true`) mới có nút "Gộp lại" riêng (Q7); chỉ tin `st.mergeOutdated` (Q2).
  if (st && st.stale !== true && st.mergeOutdated === true) {
    mk('merge-outdated', 'warning', MERGE_OUTDATED_TITLE,
      locked ? 'Bản đã chốt vẫn đang khoá. Gộp lại không đọc lại nguyên tác (ý bạn thêm tay được giữ) nhưng sẽ đưa khung xương về nháp — cần chốt lại; chốt lại bản cũ này sẽ bị chặn.'
        : 'Gộp lại không đọc lại nguyên tác; ý bạn thêm tay được giữ. Chặn chốt cho tới khi gộp lại.', 'merge', 'Gộp lại tổng quan');
  }
  if (st?.skeletonChanged) mk('skeleton-changed', 'warning', SKELETON_CHANGED_TEXT, '', 'merge', 'Gộp lại tổng quan', st.stale === true ? MERGE_BLOCKED_BY_STALE : null);
  const bad = unverifiedReferenced(ov);
  const nBad = st ? Math.max(st.unverifiedCount, bad.length) : bad.length;
  if (nBad > 0) mk('unverified', 'error', `Còn ${nBad} ý chưa tìm thấy trích trong nguyên tác`, 'Sửa trích hoặc xoá ý trước khi chốt. Ý không có trích chống lưng không được vào phim.', 'unverified', 'Xem các ý');
  if (st && st.brokenReferences > 0) mk('broken', 'error', `Còn ${st.brokenReferences} tham chiếu hỏng`, 'Hồ sơ trỏ nhân vật đã mất, quan hệ thiếu đầu hoặc ý không còn — sửa trước khi chốt (Gộp lại thường sửa được).', st.skeletonChanged ? 'merge' : null, st.skeletonChanged ? 'Gộp lại tổng quan' : '', st.skeletonChanged && st.stale === true ? MERGE_BLOCKED_BY_STALE : null);
  if (failed && failureMatters(ov, failed)) mk('last-failed', 'error', 'Lần tạo gần nhất bị lỗi', `${str(failed.note) || 'Không rõ nguyên nhân'} — tổng quan hiện tại giữ nguyên.`, 'regenerate', 'Thử lại');
  return out;
};

// ─── Hồ sơ nhân vật ───────────────────────────────────────────────────────────────────────────────────────

export type ProfileFilter = 'all' | 'main' | 'cut-unmapped';
export const PROFILE_FILTER_LABEL: Record<ProfileFilter, string> = { all: 'Tất cả', main: 'Nhân vật chính', 'cut-unmapped': 'Đã cắt / gộp / chưa ánh xạ' };

export interface ProfileFieldView { key: string; label: string; claims: ClaimView[] }
export interface ProfileCard {
  id: string; title: string; sub: string; sourceName: string; characterId: string; disposition: OverviewDisposition; dispositionText: string; dispositionTone: 'ok' | 'warn' | 'bad' | 'neutral';
  tier: string; tierText: string; fields: ProfileFieldView[]; appearanceNote: string; unverified: number; relationCount: number; firstChapter: number | null;
}

const tierOf = (p: OverviewProfile, chars: ReadonlyArray<OutlineCharacter>): string => (p.characterId ? chars.find((c) => c.id === p.characterId)?.tier ?? 'none' : 'none');

export const profileCards = (
  ov: Overview, outline: FilmOutline | null | undefined, o: { mode: NameMode; query?: string; filter?: ProfileFilter },
): { groups: Array<{ key: string; label: string; cards: ProfileCard[] }>; total: number; shown: number } => {
  const chars = outline?.characters ?? [];
  const pairs = o.mode === 'film' ? buildNamePairs(chars) : [];
  const anchors = new Map((ov.anchors ?? []).map((a) => [a.id, a] as const));
  const q = noMarks(o.query ?? '');
  const filter = o.filter ?? 'all';
  const relCount = new Map<string, number>();
  for (const r of ov.relationships ?? []) { relCount.set(r.aProfileId, (relCount.get(r.aProfileId) ?? 0) + 1); relCount.set(r.bProfileId, (relCount.get(r.bProfileId) ?? 0) + 1); }
  const allCards = (ov.profiles ?? []).map((p): ProfileCard => {
    const t = profileTitle(p, chars, o.mode);
    const tier = tierOf(p, chars);
    const f = (key: string, label: string, ids: string[]): ProfileFieldView => ({ key, label, claims: claimsOf(ov, ids, pairs).claims });
    const fields = [
      f('roleLabel', 'Vai', p.roleLabel), f('roleTitle', 'Chức danh / thân phận', p.roleTitle), f('setup', 'Xuất thân / thiết lập', p.setup), f('personality', 'Tính cách', p.personality),
      f('behavior', 'Bằng chứng hành vi', p.behavior ?? []),
      f('appearance.current', 'Ngoại hình hiện tại', p.appearance?.current ?? []), f('appearance.before', 'Ngoại hình trước khi đổi', p.appearance?.before ?? []), f('appearance.after', 'Ngoại hình sau khi đổi', p.appearance?.after ?? []),
    ];
    const allIds = [...p.personality, ...(p.behavior ?? []), ...p.setup, ...p.roleLabel, ...p.roleTitle, ...(p.appearance?.current ?? []), ...(p.appearance?.before ?? []), ...(p.appearance?.after ?? [])];
    const cm = claimMap(ov);
    return {
      id: p.id, title: t.title, sub: t.sub, sourceName: p.sourceName, characterId: p.characterId, disposition: p.disposition,
      dispositionText: p.disposition === 'MERGED' && p.characterId ? `Đã gộp vào ${profileTitle({ sourceName: p.sourceName, characterId: p.characterId }, chars, 'film').title}` : DISPOSITION_TEXT[p.disposition],
      dispositionTone: DISPOSITION_TONE[p.disposition], tier, tierText: TIER_TEXT[tier] ?? TIER_TEXT.none, fields, appearanceNote: swapNames(str(p.appearance?.note), pairs),
      unverified: allIds.filter((id) => { const c = cm.get(id); return !!c && c.status !== 'ok'; }).length, relationCount: relCount.get(p.id) ?? 0,
      firstChapter: p.firstAppearanceAnchorId ? anchors.get(p.firstAppearanceAnchorId)?.orderIndex ?? null : null,
    };
  });
  const matches = (c: ProfileCard): boolean => {
    if (filter === 'main' && c.tier !== 'lead') return false;
    if (filter === 'cut-unmapped' && c.disposition === 'KEPT') return false;
    if (!q) return true;
    const hay = noMarks([c.title, c.sub, c.sourceName, ...c.fields.filter((x) => x.key === 'roleLabel' || x.key === 'roleTitle').flatMap((x) => x.claims.map((y) => y.text))].join(' '));
    return hay.includes(q);
  };
  const shownCards = allCards.filter(matches);
  const order = ['lead', 'support', 'functional', 'none'];
  const groups = order.map((k) => ({ key: k, label: TIER_TEXT[k], cards: shownCards.filter((c) => c.tier === k) })).filter((g) => g.cards.length > 0);
  return { groups, total: allCards.length, shown: shownCards.length };
};

// ─── Chiến lược tập 1, đạo cụ, luật, câu V.O. ─────────────────────────────────────────────────────────────────

export interface IntroLineView { id: string; purposeText: string; subject: string; text: string; basis: ClaimView[]; availableFromChapter: number | null }
export interface StrategyView {
  approach: string;
  introductions: Array<{ profileId: string; who: string; how: string; basis: ClaimView[]; introLine: IntroLineView | null }>;
  introLines: IntroLineView[];
}

export const strategyView = (ov: Overview, outline: FilmOutline | null | undefined, mode: NameMode): StrategyView => {
  const chars = outline?.characters ?? [];
  const pairs = mode === 'film' ? buildNamePairs(chars) : [];
  const profiles = new Map((ov.profiles ?? []).map((p) => [p.id, p] as const));
  const rules = new Map((ov.worldRules ?? []).map((w) => [w.id, w] as const));
  const anchors = new Map((ov.anchors ?? []).map((a) => [a.id, a] as const));
  const lineView = (l: Overview['introLines'][number]): IntroLineView => {
    const subject = l.purpose === 'CHARACTER'
      ? (profiles.get(l.subjectId) ? profileTitle(profiles.get(l.subjectId) as OverviewProfile, chars, mode).title : l.subjectId)
      : (rules.get(l.subjectId)?.title ?? l.subjectId);
    return {
      id: l.id, purposeText: l.purpose === 'CHARACTER' ? 'Giới thiệu nhân vật' : 'Giới thiệu luật thế giới', subject, text: l.text,   // câu V.O. đã viết bằng ngôn ngữ phim — không thay tên
      basis: claimsOf(ov, l.basisClaimIds, pairs).claims, availableFromChapter: l.availableFromAnchorId ? anchors.get(l.availableFromAnchorId)?.orderIndex ?? null : null,
    };
  };
  const lines = new Map((ov.introLines ?? []).map((l) => [l.id, lineView(l)] as const));
  return {
    approach: swapNames(str(ov.firstEpisodeStrategy?.approach), pairs),
    introductions: (ov.firstEpisodeStrategy?.introductions ?? []).map((i) => ({
      profileId: i.profileId, who: profiles.get(i.profileId) ? profileTitle(profiles.get(i.profileId) as OverviewProfile, chars, mode).title : i.profileId,
      how: swapNames(str(i.how), pairs), basis: claimsOf(ov, i.basisClaimIds, pairs).claims, introLine: i.introLineId ? lines.get(i.introLineId) ?? null : null,
    })),
    introLines: Array.from(lines.values()),
  };
};

// ─── Mục "Động cơ hấp dẫn" ────────────────────────────────────────────────────────────────────────────────────

export interface MotiveNoteView {
  id: string; group: MotiveGroup; text: string; basis: ClaimView[]; missingBasis: number;
  /** Số ý nền chưa `ok` (ghi chú dựa trên ý chưa tìm thấy trích ⇒ chặn chốt như ý thường). */
  unverified: number;
  availableFromChapter: number | null;
  /** "dùng được từ chương N" · "chưa xác định mốc tiết lộ" (fail-closed: 4c-2c không đưa vào writer). */
  availableText: string;
}
export interface MotiveGroupView { group: MotiveGroup; label: string; help: string; badge: string; notes: MotiveNoteView[]; canAdd: boolean }
export interface MotivesView {
  /** `false` ⇒ tổng quan chưa có khối (bản trước C33). KHÔNG dùng để quyết "gộp theo cách cũ" — việc đó chỉ theo `overviewState.mergeOutdated` (Q2). */
  exists: boolean;
  goldenFinger: { badge: string; claims: ClaimView[]; missing: number; canAdd: boolean };
  groups: MotiveGroupView[];
  noteCount: number;
}

export const motivesView = (ov: Overview, outline: FilmOutline | null | undefined, mode: NameMode): MotivesView => {
  const pairs = mode === 'film' ? buildNamePairs(outline?.characters) : [];
  const anchors = new Map((ov.anchors ?? []).map((a) => [a.id, a] as const));
  const m = ov.motives;
  const gf = claimsOf(ov, m?.goldenFinger?.claimIds ?? [], pairs);
  const groups = MOTIVE_GROUPS.map((g): MotiveGroupView => {
    const notes = (m?.[g.group] ?? []).map((n): MotiveNoteView => {
      const basis = claimsOf(ov, n.basisClaimIds, pairs);
      const ch = n.availableFromAnchorId ? anchors.get(n.availableFromAnchorId)?.orderIndex ?? null : null;
      return {
        id: n.id, group: g.group, text: swapNames(str(n.text), pairs), basis: basis.claims, missingBasis: basis.missing, unverified: basis.claims.filter((c) => c.status !== 'ok').length,
        availableFromChapter: ch, availableText: ch !== null ? `Dùng được từ chương ${ch}` : 'Chưa xác định mốc tiết lộ',
      };
    });
    return { group: g.group, label: g.label, help: g.help, badge: MOTIVE_NOTE_LABEL, notes, canAdd: notes.length < MOTIVE_NOTES_MAX };
  });
  return {
    exists: !!m, goldenFinger: { badge: MOTIVE_FACT_LABEL, claims: gf.claims, missing: gf.missing, canAdd: gf.claims.length + gf.missing < GOLDEN_FINGER_MAX },
    groups, noteCount: groups.reduce((n, g) => n + g.notes.length, 0),
  };
};

export interface PropView { id: string; name: string; aliases: string[]; importance: string; claims: ClaimView[]; chapters: number[] }
export const propViews = (ov: Overview, outline: FilmOutline | null | undefined, mode: NameMode): PropView[] => {
  const pairs = mode === 'film' ? buildNamePairs(outline?.characters) : [];
  const anchors = new Map((ov.anchors ?? []).map((a) => [a.id, a] as const));
  return (ov.keyProps ?? []).map((p) => ({
    id: p.id, name: p.name, aliases: p.aliases ?? [], importance: swapNames(str(p.importance), pairs), claims: claimsOf(ov, p.description, pairs).claims,
    chapters: Array.from(new Set((p.occurrenceAnchorIds ?? []).map((id) => anchors.get(id)?.orderIndex).filter((n): n is number => typeof n === 'number'))).sort((a, b) => a - b),
  }));
};
export interface RuleView { id: string; title: string; claims: ClaimView[] }
export const ruleViews = (ov: Overview, outline: FilmOutline | null | undefined, mode: NameMode): RuleView[] => {
  const pairs = mode === 'film' ? buildNamePairs(outline?.characters) : [];
  return (ov.worldRules ?? []).map((w) => ({ id: w.id, title: w.title, claims: claimsOf(ov, w.claimIds, pairs).claims }));
};

export interface PlotPartView { key: OverviewPlotKey; label: string; status: 'KNOWN' | 'NOT_REACHED' | 'UNKNOWN'; statusText: string; note: string; claims: ClaimView[] }
export const plotViews = (ov: Overview, outline: FilmOutline | null | undefined, mode: NameMode): PlotPartView[] => {
  const pairs = mode === 'film' ? buildNamePairs(outline?.characters) : [];
  return PLOT_KEYS.map((k) => {
    const p = ov.plot?.[k] ?? { status: 'UNKNOWN' as const, claimIds: [], note: '' };
    return { key: k, label: PLOT_LABEL[k], status: p.status, statusText: PLOT_STATUS_TEXT[p.status], note: swapNames(str(p.note), pairs), claims: claimsOf(ov, p.claimIds, pairs).claims };
  });
};
export const synopsisClaims = (ov: Overview, outline: FilmOutline | null | undefined, mode: NameMode): ClaimView[] =>
  claimsOf(ov, ov.synopsis, mode === 'film' ? buildNamePairs(outline?.characters) : []).claims;

// ─── Quan hệ: câu đọc được + dữ liệu cho sơ đồ ────────────────────────────────────────────────────────────────

export const relationSentence = (type: OverviewRelationshipType, a: string, b: string): string => {
  switch (type) {
    case 'parent-child': return `${a} là cha/mẹ của ${b}`;
    case 'sibling': return `${a} và ${b} là anh chị em`;
    case 'spouse': return `${a} và ${b} là vợ chồng`;
    case 'kin': return `${a} và ${b} có quan hệ họ hàng`;
    case 'same-faction': return `${a} và ${b} cùng phe`;
    case 'master-servant': return `${a} là chủ của ${b}`;
    case 'ally': return `${a} và ${b} là đồng minh`;
    case 'rival': return `${a} và ${b} là đối thủ`;
    default: return `${a} — ${b}`;
  }
};

export interface RelationRow { id: string; aId: string; bId: string; a: string; b: string; type: OverviewRelationshipType; sentence: string; factionLabel: string; claims: ClaimView[]; verified: boolean }

export const relationRows = (ov: Overview, outline: FilmOutline | null | undefined, mode: NameMode): RelationRow[] => {
  const chars = outline?.characters ?? [];
  const pairs = mode === 'film' ? buildNamePairs(chars) : [];
  const profiles = new Map((ov.profiles ?? []).map((p) => [p.id, p] as const));
  const factions = new Map((ov.factions ?? []).map((f) => [f.id, f] as const));
  const nameOf = (id: string): string => (profiles.get(id) ? profileTitle(profiles.get(id) as OverviewProfile, chars, mode).title : id);
  return (ov.relationships ?? []).map((r: OverviewRelationship) => {
    const claims = claimsOf(ov, r.claimIds, pairs).claims;
    return {
      id: r.id, aId: r.aProfileId, bId: r.bProfileId, a: nameOf(r.aProfileId), b: nameOf(r.bProfileId), type: r.type, sentence: relationSentence(r.type, nameOf(r.aProfileId), nameOf(r.bProfileId)),
      factionLabel: r.factionId ? factions.get(r.factionId)?.label ?? '' : '', claims, verified: claims.length > 0 && claims.every((c) => c.status === 'ok'),
    };
  });
};

const rankOfTier = (tier: string): number => TIER_RANK[tier] ?? 3;

/** Dữ liệu cho sơ đồ từ TỔNG QUAN (có trích). Mỗi hồ sơ là một người; phe = `factions` của tổng quan (người thuộc nhiều phe ⇒ phe đầu). */
export const graphFromOverview = (ov: Overview, outline: FilmOutline | null | undefined, mode: NameMode): GraphInput => {
  const chars = outline?.characters ?? [];
  const groupOf = new Map<string, string>();
  for (const f of ov.factions ?? []) for (const m of f.memberProfileIds ?? []) if (!groupOf.has(m)) groupOf.set(m, f.id);
  const nodes: GraphNode[] = (ov.profiles ?? []).map((p) => ({
    id: p.id, label: profileTitle(p, chars, mode).title, group: groupOf.get(p.id) ?? '', rank: rankOfTier(tierOf(p, chars)), cut: p.disposition === 'CUT' || p.disposition === 'MERGED',
  }));
  const groups: GraphGroup[] = (ov.factions ?? []).map((f: OverviewFaction) => ({ id: f.id, label: f.label }));
  const edges: GraphEdge[] = (ov.relationships ?? []).map((r) => ({ id: r.id, a: r.aProfileId, b: r.bProfileId, type: r.type }));
  return { nodes, edges, groups };
};

/**
 * Dữ liệu cho sơ đồ từ quan hệ ③ cũ (`outline.relationships` — chưa có trích, nhãn "chưa xác minh trích"). Chỉ lấy nhân vật có mặt trong ít nhất một quan hệ.
 * `null` nếu khung xương không có quan hệ nào.
 */
export const graphFromLegacy = (outline: FilmOutline | null | undefined, mode: NameMode): GraphInput | null => {
  const rels = outline?.relationships ?? [];
  if (!rels.length) return null;
  const chars = outline?.characters ?? [];
  const byId = new Map(chars.map((c) => [c.id, c] as const));
  const involved = new Set<string>();
  for (const r of rels) { involved.add(r.a); involved.add(r.b); }
  const groupOf = new Map<string, string>();
  for (const f of outline?.factions ?? []) for (const m of f.memberIds ?? []) if (!groupOf.has(m)) groupOf.set(m, f.id);
  const nameOf = (c: OutlineCharacter): string => (mode === 'film' && str(c.localizedName) ? str(c.localizedName) : c.name);
  const nodes: GraphNode[] = Array.from(involved).filter((id) => byId.has(id)).map((id) => {
    const c = byId.get(id) as OutlineCharacter;
    return { id, label: nameOf(c), group: groupOf.get(id) ?? '', rank: rankOfTier(c.tier), cut: false };
  });
  const edges: GraphEdge[] = rels.map((r, i) => ({ id: `lr${i + 1}`, a: r.a, b: r.b, type: r.type }));
  return { nodes, edges, groups: (outline?.factions ?? []).map((f) => ({ id: f.id, label: f.label })) };
};

export interface LegacyRelationRow { id: string; sentence: string; factionLabel: string; aId: string; bId: string; type: OverviewRelationshipType }
/** Danh sách quan hệ ③ cũ (chưa có trích) — đi cùng `graphFromLegacy`: CÙNG id `lr<n>` (n = vị trí trong `outline.relationships`, kể cả quan hệ bị lọc vì thiếu người). */
export const legacyRelationRows = (outline: FilmOutline | null | undefined, mode: NameMode): LegacyRelationRow[] => {
  const byId = new Map((outline?.characters ?? []).map((c) => [c.id, c] as const));
  const factions = new Map((outline?.factions ?? []).map((f) => [f.id, f] as const));
  const nm = (id: string): string => { const c = byId.get(id); return c ? (mode === 'film' && str(c.localizedName) ? str(c.localizedName) : c.name) : id; };
  return (outline?.relationships ?? []).map((r, i) => ({ r, id: `lr${i + 1}` })).filter(({ r }) => byId.has(r.a) && byId.has(r.b))
    .map(({ r, id }) => ({ id, sentence: relationSentence(r.type, nm(r.a), nm(r.b)), factionLabel: r.factionId ? factions.get(r.factionId)?.label ?? '' : '', aId: r.a, bId: r.b, type: r.type }));
};

/** Người nên được chọn sẵn: hạng nhỏ nhất, nhiều quan hệ nhất, rồi tên. Không có quan hệ nào ⇒ null (xem toàn cảnh). */
export const defaultFocusId = (g: GraphInput): string | null => {
  if (!g.edges.length) return null;
  const deg = new Map<string, number>();
  for (const e of g.edges) { deg.set(e.a, (deg.get(e.a) ?? 0) + 1); deg.set(e.b, (deg.get(e.b) ?? 0) + 1); }
  const cands = g.nodes.filter((n) => (deg.get(n.id) ?? 0) > 0)
    .sort((a, b) => a.rank - b.rank || (deg.get(b.id) ?? 0) - (deg.get(a.id) ?? 0) || (a.label < b.label ? -1 : a.label > b.label ? 1 : 0) || (a.id < b.id ? -1 : 1));
  return cands[0]?.id ?? null;
};

// ─── Tiến độ job tạo tổng quan ────────────────────────────────────────────────────────────────────────────

export const OVERVIEW_STEP_ORDER = ['EXTRACT', 'VERIFY', 'MERGE', 'SAVE'] as const;
export const OVERVIEW_STEP_LABEL: Record<string, string> = { EXTRACT: 'Đọc từng quyển', VERIFY: 'Xác minh trích', MERGE: 'Gộp thành tổng quan', SAVE: 'Lưu' };

export interface ProgressStep { key: string; label: string; state: 'done' | 'run' | 'todo' }
export interface ProgressView { steps: ProgressStep[]; line: string; known: boolean }

/** `detail.progress` của job (qua `workbench.jobs.running[].progress`). Chưa có tiến độ ⇒ `known:false` ("Đang bắt đầu…"). */
export const progressView = (p: JobStepProgress | null | undefined): ProgressView => {
  const idx = p ? OVERVIEW_STEP_ORDER.indexOf(str(p.step) as typeof OVERVIEW_STEP_ORDER[number]) : -1;
  const steps = OVERVIEW_STEP_ORDER.map((k, i): ProgressStep => ({ key: k, label: OVERVIEW_STEP_LABEL[k], state: idx < 0 ? 'todo' : i < idx ? 'done' : i === idx ? 'run' : 'todo' }));
  const label = p && str(p.label) ? str(p.label) : idx >= 0 ? OVERVIEW_STEP_LABEL[OVERVIEW_STEP_ORDER[idx]] : '';
  const count = p && typeof p.current === 'number' && typeof p.total === 'number' && p.total > 0 && !/\d+\s*\/\s*\d+/.test(label) ? ` (${Math.min(p.current, p.total)}/${p.total})` : '';
  return { steps, line: label ? `${label}${count}` : 'Đang bắt đầu…', known: idx >= 0 };
};

/** Tóm tắt kết quả job (từ `detail.result`) — nói thẳng những gì bị bỏ/thiếu. */
export interface ResultSummary { tone: 'success' | 'warning' | 'error'; title: string; lines: string[] }
export const resultSummary = (r: OverviewJobResult | null | undefined): ResultSummary | null => {
  if (!r) return null;
  if (!r.saved) return { tone: 'error', title: 'Không lưu được tổng quan', lines: ['Tổng quan cũ (nếu có) được giữ nguyên.'] };
  const lines: string[] = [];
  let tone: ResultSummary['tone'] = 'success';
  const warn = (t: string): void => { tone = 'warning'; lines.push(t); };
  if (r.missingVolumes?.length) warn(`Thiếu quyển ${r.missingVolumes.join(', ')} — chưa đọc được; chưa chốt được cho tới khi "Tạo lại".`);
  if (r.droppedManualClaims > 0) warn(`${r.droppedManualClaims} ý bạn thêm tay đã mất vì đọc lại nguyên tác (dùng "Gộp lại" để giữ ý thêm tay).`);
  if (r.droppedClaims > 0) warn(`${r.droppedClaims} ý bị bỏ vì sai định dạng hoặc quá dài.`);
  if (r.mergeDropped > 0) warn(`${r.mergeDropped} chỗ trong bước gộp bị loại vì tham chiếu sai.`);
  if (r.emptyVolumes?.length) lines.push(`Quyển ${r.emptyVolumes.join(', ')}: đọc xong nhưng không có ý nào (không phải quyển thiếu).`);
  lines.push(`${r.claims} ý · ${r.profiles} hồ sơ · quyển: đọc mới ${r.volumes?.extracted ?? 0}, dùng lại ${r.volumes?.reused ?? 0}${r.volumes?.failed ? `, lỗi ${r.volumes.failed}` : ''}.`);
  lines.push(r.approvalKept ? 'Nội dung không đổi — giữ nguyên bản đã chốt.' : r.status === 'DRAFT' ? 'Khung xương ở bản nháp — cần "Chốt tổng quan".' : 'Khung xương đang đã duyệt.');
  return { tone, title: tone === 'success' ? 'Đã tạo xong tổng quan' : 'Đã tạo xong tổng quan — có điểm cần xem', lines };
};

// ─── Nút tạo / tạo lại / đọc lại / gộp lại ─────────────────────────────────────────────────────────────────

export interface ActionCtx { status: string | undefined; hasOverview: boolean; estimate: OverviewEstimate | null | undefined; hasManualClaims: boolean }

const kchars = (n: number): string => (n < 1000 ? '<1' : String(Math.round(n / 1000)));

/** "tốn AI · đọc N quyển · ~X nghìn ký tự" — KHÔNG hiện tiền (Q6). `estimate` null ⇒ nói thật "chưa ước được" (không viết 0 quyển). */
export const estimateLine = (mode: OverviewMode, ctx: Pick<ActionCtx, 'hasOverview' | 'estimate'>): string => {
  if (mode === 'merge') return 'tốn AI · gộp lại (không đọc lại nguyên tác)';
  const e = ctx.estimate;
  if (!e) return 'tốn AI · chưa ước được khối lượng đọc';
  if (mode === 'force') return `tốn AI · đọc lại toàn bộ ${e.totalVolumes} quyển · ~${kchars(e.totalChars)} nghìn ký tự`;
  if (ctx.hasOverview && e.volumes === 0) return 'tốn AI · không quyển nào cần đọc lại — chỉ gộp lại';
  return `tốn AI · đọc ${e.volumes} quyển · ~${kchars(e.chars)} nghìn ký tự`;
};

export interface ActionConfirm { title: string; okText: string; danger: boolean; lines: string[] }

export const ACTION_LABEL: Record<OverviewMode, (hasOverview: boolean) => string> = {
  full: (has) => (has ? 'Tạo lại' : 'Tạo tổng quan'), force: () => 'Đọc lại toàn bộ', merge: () => 'Gộp lại tổng quan',
};

/**
 * Hộp hỏi lại TRƯỚC khi gọi AI. Luôn có dòng chi phí; thêm: ② đang ĐÃ DUYỆT ⇒ "sẽ đưa khung xương về nháp, cần chốt lại"; full/force mà tổng quan có ý bạn thêm tay ⇒ "ý bạn thêm tay sẽ mất".
 */
export const actionConfirm = (mode: OverviewMode, ctx: ActionCtx): ActionConfirm => {
  const lines = [estimateLine(mode, ctx)];
  if (mode === 'merge') lines.push('Gộp lại các ý đã lưu theo khung xương hiện tại. Ý bạn thêm tay được giữ.');
  else if (mode === 'force') lines.push('Đọc lại TOÀN BỘ nguyên tác, bỏ kết quả đã đọc của mọi quyển.');
  else if (ctx.hasOverview) lines.push('Chỉ đọc lại các quyển đã đổi hoặc còn thiếu; quyển còn nguyên được dùng lại.');
  else lines.push('Chạy ở nền; xong sẽ có bản NHÁP để bạn đọc, sửa rồi chốt.');
  if (ctx.hasOverview) lines.push('Kết quả là một bản nháp MỚI — cần "Chốt tổng quan" lại. Bản cũ còn trong danh sách phiên bản ②.');
  if (ctx.status === 'APPROVED') lines.push('Khung xương đang đã duyệt: việc này sẽ đưa khung xương về nháp, cần chốt lại (chia tập / viết tập mới bị chặn tới khi chốt).');
  if ((mode === 'full' || mode === 'force') && ctx.hasManualClaims) lines.push('Ý bạn thêm tay sẽ mất — dùng "Gộp lại" để giữ.');
  const label = ACTION_LABEL[mode](ctx.hasOverview);
  return { title: `${label}?`, okText: label, danger: mode === 'force' || ctx.status === 'APPROVED' || ((mode === 'full') && ctx.hasManualClaims), lines };
};

/** Nút "Gộp lại" có bấm được không (BE từ chối `merge` khi nguồn/cách đọc lệch hoặc chưa có tổng quan). */
export const mergeDisabledReason = (st: OverviewStateView | undefined, hasOverview: boolean): string | null => {
  if (!hasOverview) return 'Chưa có tổng quan để gộp lại';
  if (st?.stale === true) return MERGE_BLOCKED_BY_STALE;
  return null;
};

// ─── Nút Chốt tổng quan ───────────────────────────────────────────────────────────────────────────────────

export interface ApproveView {
  /** true ⇒ luật tổng quan áp dụng (đã có tổng quan hoặc truyện mới bắt buộc). Truyện cũ ⇒ false ⇒ giữ "Duyệt khung xương". */
  usesOverview: boolean;
  approveLabel: string; unapproveLabel: string; continueLabel: string;
  /** null ⇒ bấm được (phần tổng quan); lint ERROR của khung xương do caller ghép riêng. */
  blockReason: string | null;
  doneText: string;
}

export const approveView = (record: Pick<OutlineRecord, 'outline' | 'overviewState'> | null | undefined): ApproveView => {
  const st = record?.overviewState;
  const ov = record?.outline?.overview;
  const uses = !!ov || !!st?.exists || !!st?.required || !!record?.outline?.params?.overviewRequired;
  if (!uses) return { usesOverview: false, approveLabel: 'Duyệt khung xương', unapproveLabel: 'Bỏ duyệt', continueLabel: 'Duyệt & chạy tiếp', blockReason: null, doneText: 'Đã duyệt — giờ có thể viết phân tập' };
  let block: string | null = null;
  if (!ov) block = 'Cần tạo "Tổng quan truyện" trước (tab đầu của ②) rồi mới chốt được';
  else if (st) {
    if (st.missingVolumes.length) block = `Thiếu quyển ${st.missingVolumes.join(', ')} — "Tạo lại" để đọc các quyển thiếu`;
    else if (st.stale === true) block = st.staleCauses.includes('CONTENT') && !st.staleCauses.includes('READER') ? STALE_CONTENT_TEXT : st.staleCauses.includes('READER') && !st.staleCauses.includes('CONTENT') ? STALE_READER_TEXT : 'Nguyên tác hoặc cách đọc đã đổi — tạo lại tổng quan';
    else if (st.mergeOutdated === true) block = MERGE_OUTDATED_TEXT;
    else if (st.skeletonChanged) block = 'Khung xương đã đổi sau khi gộp tổng quan — bấm "Gộp lại tổng quan"';
    else if (st.brokenReferences > 0) block = `Tổng quan còn ${st.brokenReferences} tham chiếu hỏng — sửa trước`;
    else if (st.unverifiedCount > 0) block = `Còn ${st.unverifiedCount} ý chưa tìm thấy trích — sửa trích hoặc xoá ý trước`;
  }
  return { usesOverview: true, approveLabel: 'Chốt tổng quan', unapproveLabel: 'Mở để sửa — cần chốt lại', continueLabel: 'Chốt tổng quan & chạy tiếp', blockReason: block, doneText: 'Đã chốt tổng quan — giờ có thể chia tập' };
};

/** Lý do tắt nút Duyệt/Chốt: lỗi lint của khung xương trước (đã có sẵn), rồi lý do tổng quan. */
export const approveBlock = (lint: { can: boolean; reason?: string }, v: ApproveView): string | null => (lint.can ? v.blockReason : (lint.reason || 'Còn lỗi ở khung xương — sửa hết lỗi trước'));
